const CLARITY_SCRIPT_ID = "clarity-script";
const PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID || "";
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

const isDev = process.env.NODE_ENV === "development";

// ── Debug logging ──────────────────────────────────────────────────────────
const debugLog = (msg: string, data?: any) => {
  if (isDev) {
    const prefix = "%c[Clarity]";
    const style = "color:#7c3aed;font-weight:bold";
    if (data !== undefined) {
      console.log(prefix, style, msg, data);
    } else {
      console.log(prefix, style, msg);
    }
  }
};

const debugWarn = (msg: string, data?: any) => {
  if (isDev) {
    const prefix = "%c[Clarity]";
    const style = "color:#d97706;font-weight:bold";
    if (data !== undefined) {
      console.warn(prefix, style, msg, data);
    } else {
      console.warn(prefix, style, msg);
    }
  }
};

const debugError = (msg: string, data?: any) => {
  if (isDev) {
    const prefix = "%c[Clarity]";
    const style = "color:#dc2626;font-weight:bold";
    if (data !== undefined) {
      console.error(prefix, style, msg, data);
    } else {
      console.error(prefix, style, msg);
    }
  }
};

// Structures development-only debug console logs for audit / verification
const logDebug = (action: string, details?: any) => {
  if (isDev) {
    const timestamp = new Date().toISOString();
    const route = typeof window !== "undefined" ? window.location.pathname : "";
    console.log(
      `%c[Clarity DEBUG] [${timestamp}] [Route: ${route}] ${action}`,
      "color:#7c3aed;font-weight:bold;font-size:12px",
      details || ""
    );
  }
};

interface ClarityWindow extends Window {
  clarity?: (...args: any[]) => void;
}

const getClarity = (): ((...args: any[]) => void) | undefined => {
  if (typeof window !== "undefined") {
    return (window as unknown as ClarityWindow).clarity;
  }
  return undefined;
};

let isInitializationStarted = false;
let isInitialized = false;
let scriptLoaded = false;
let scriptLoadFailed = false;
let queuedCallCount = 0;
let lastIdentifiedUserId = "";


let lastTrackedPagePath = "";

// ── Diagnostics state (exposed for the dev diagnostic tool) ────────────────
export const diagnostics = {
  scriptLoaded,
  scriptLoadFailed,
  isInitialized,
  projectIdMasked: PROJECT_ID ? PROJECT_ID.slice(0, 3) + "***" : "(empty)",
  clarityAvailable: false,
  identifyCompleted: false,
  pageViewCount: 0,
  totalEventsSent: 0,
};

const updateDiagnostics = () => {
  diagnostics.scriptLoaded = scriptLoaded;
  diagnostics.scriptLoadFailed = scriptLoadFailed;
  diagnostics.isInitialized = isInitialized;
  diagnostics.clarityAvailable = typeof getClarity() === "function";
};

const maskProjectId = (id: string): string => {
  if (!id) return "(empty)";
  if (id.length <= 6) return id.slice(0, 2) + "***";
  return id.slice(0, 3) + "***" + id.slice(-2);
};

export const initializeClarity = (): void => {
  if (isInitializationStarted || isInitialized) return;
  if (!PROJECT_ID) {
    debugWarn("CLARITY_PROJECT_ID is not set. Skipping initialization.");
    return;
  }
  if (typeof window === "undefined") return;

  isInitializationStarted = true;

  if (document.getElementById(CLARITY_SCRIPT_ID)) {
    debugLog("Script tag already exists, skipping.");
    return;
  }

  debugLog("Initializing Clarity...", { projectId: maskProjectId(PROJECT_ID) });

  // Create the queuing shim (required by Clarity before the script loads)
  const win = window as any;
  win.clarity =
    win.clarity ||
    function () {
      (win.clarity.q = win.clarity.q || []).push(arguments);
      queuedCallCount++;
    };

  logDebug("SDK initialized (queuing shim ready)");

  // Listen for JS errors & unhandled promise rejections
  const handleJSError = (event: ErrorEvent) => {
    trackEvent("js_error", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  };
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    trackEvent("unhandled_rejection", {
      message: reason instanceof Error ? reason.message : String(reason),
    });
  };
  window.addEventListener("error", handleJSError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  // Create and inject the script tag
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://www.clarity.ms/tag/" + PROJECT_ID;
  script.id = CLARITY_SCRIPT_ID;

  script.onload = () => {
    scriptLoaded = true;
    isInitialized = true;
    updateDiagnostics();
    logDebug("SDK loaded & ready", {
      queuedCallsProcessed: queuedCallCount,
    });
    queuedCallCount = 0;

    setCustomTag("environment", process.env.NODE_ENV || "development");
    setCustomTag("app_version", process.env.REACT_APP_VERSION || "0.1.0");
  };

  script.onerror = () => {
    scriptLoadFailed = true;
    updateDiagnostics();
    debugError(
      "Failed to load Clarity script. Check CSP, ad-blockers, or network.",
      {
        url: script.src,
      }
    );
  };

  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  // Script load timeout warning
  setTimeout(() => {
    if (!scriptLoaded && !scriptLoadFailed) {
      debugWarn(
        "Clarity script is taking longer than expected to load. Check network/ad-blocker.",
        {
          timeoutMs: SCRIPT_LOAD_TIMEOUT_MS,
        }
      );
    }
  }, SCRIPT_LOAD_TIMEOUT_MS);
};

// ── Safe wrapper for all clarity API calls ──────────────────────────────────
const safeClarityCall = (fnName: string, args: any[]): boolean => {
  const clarity = getClarity();
  if (typeof clarity !== "function") {
    if (!scriptLoadFailed) {
      debugWarn("clarity() is not available yet. Call queued or skipped.", {
        fnName,
        argsLength: args.length,
      });
    }
    return false;
  }

  try {
    clarity(...args);
    diagnostics.totalEventsSent++;
    updateDiagnostics();
    debugLog("clarity." + fnName, args);
    return true;
  } catch (err) {
    debugError("clarity." + fnName + " threw an error", err);
    return false;
  }
};

export const identifyUser = (
  userId: string,
  email?: string,
  metadata?: Record<string, string | undefined>
): void => {
  if (lastIdentifiedUserId === userId && diagnostics.identifyCompleted) {
    debugLog("User already identified in this session. Skipping duplicate call.", { userId });
    return;
  }

  const sent = safeClarityCall("identify", [
    "identify",
    userId,
    undefined,
    undefined,
    email,
  ]);
  if (sent) {
    lastIdentifiedUserId = userId;
    diagnostics.identifyCompleted = true;
    updateDiagnostics();
    logDebug("User identified", { userId, email, metadata });
  }

  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        setCustomTag(key, value);
      }
    });
  }
};

export const setCustomTag = (key: string, value: string): void => {
  safeClarityCall("set", ["set", key, value]);
};

export const trackEvent = (
  eventName: string,
  eventData?: Record<string, string | number | boolean>
): void => {
  if (eventData) {
    const eventString = Object.entries(eventData)
      .map(([k, v]) => k + ":" + v)
      .join("|");
    safeClarityCall("event", ["event", eventName + "__" + eventString]);
  } else {
    safeClarityCall("event", ["event", eventName]);
  }

  logDebug("Custom event fired", { eventName, eventData });

  if (eventName === "page_view") {
    diagnostics.pageViewCount++;
    updateDiagnostics();
  }
};

export const trackRouteChange = (path: string): void => {
  if (path === lastTrackedPagePath) return;
  lastTrackedPagePath = path;
  setCustomTag("current_route", path);
  trackEvent("page_view", { path });
  logDebug("Page tracked", { path });
};

export const clearIdentity = (): void => {
  safeClarityCall("identify", [
    "identify",
    "",
    undefined,
    undefined,
    undefined,
  ]);
  lastIdentifiedUserId = "";
  diagnostics.identifyCompleted = false;
  updateDiagnostics();
  logDebug("Identity cleared");
};

export const isClarityReady = (): boolean => {
  return typeof getClarity() === "function";
};

export const setClarityTags = (tags: Record<string, string>): void => {
  Object.entries(tags).forEach(([key, value]) => {
    setCustomTag(key, value);
  });
};

export const trackFormInteraction = (
  formName: string,
  action: "opened" | "submitted" | "validation_error" | "cancelled",
  details?: string
): void => {
  trackEvent("form_interaction", {
    form: formName,
    action,
    ...(details ? { details } : {}),
  });
};

export const trackModalInteraction = (
  modalName: string,
  action: "opened" | "closed"
): void => {
  trackEvent("modal_interaction", { modal: modalName, action });
};

export const trackNavigation = (destination: string, source?: string): void => {
  trackEvent("navigation", { destination, ...(source ? { source } : {}) });
};

// Debounce state to avoid spamming clarity on every keystroke
const searchDebounceTimeouts: Record<string, any> = {};

export const trackSearch = (
  searchType: string,
  queryLength: number,
  hasResults: boolean
): void => {
  if (searchDebounceTimeouts[searchType]) {
    clearTimeout(searchDebounceTimeouts[searchType]);
  }
  searchDebounceTimeouts[searchType] = setTimeout(() => {
    trackEvent("search_performed", {
      type: searchType,
      query_length: queryLength,
      has_results: hasResults,
    });
  }, 1000);
};

export const ClarityEvents = {
  LOGIN: "login",
  LOGOUT: "logout",
  REGISTRATION_COMPLETED: "registration_completed",
  REGISTRATION_STARTED: "registration_started",
  PAGE_VIEW: "page_view",
  ROOM_CREATED: "room_created",
  ROOM_JOINED: "room_joined",
  ROOM_SEARCH: "room_search",
  ARTICLE_SEARCH: "article_search",
  ARTICLE_PAGINATION: "article_pagination",
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_COMPLETED: "checkout_completed",
  SUBSCRIPTION_STARTED: "subscription_started",
  SUBSCRIPTION_COMPLETED: "subscription_completed",
  PROFILE_UPDATED: "profile_updated",
  PROFILE_DELETED: "profile_deleted",
  FOLLOW_PROFILE: "follow_profile",
  UNFOLLOW_PROFILE: "unfollow_profile",
  PAYMENT_STARTED: "payment_started",
  PAYMENT_COMPLETED: "payment_completed",
  FORM_INTERACTION: "form_interaction",
  MODAL_INTERACTION: "modal_interaction",
  NAVIGATION: "navigation",
  SEARCH_PERFORMED: "search_performed",
  API_FAILURE: "api_failure",
  ERROR_PAGE: "error_page",
  EXTERNAL_LINK: "external_link_click",
  VIDEO_UPLOAD_STARTED: "video_upload_started",
  VIDEO_UPLOAD_COMPLETED: "video_upload_completed",
};

export { getClarity };
